<template>
    <Teleport to="body">
        <Transition name="modal">
            <div v-if="isOpen"
                 @click.self="$emit('close', 'backdrop')"
                 class="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center p-4 z-[999991]"
            >
                <div class="max-w-md w-full bg-white rounded-3xl shadow-xl overflow-hidden relative max-h-[90vh] overflow-y-auto">

                    <!-- Close Button -->
                    <button
                        @click="$emit('close', 'x')"
                        class="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/20 hover:bg-black/30 transition-colors"
                    >
                        <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                    </button>

                    <!-- Header Section -->
                    <div class="bg-amber-500 px-8 py-6 text-center">
                        <img
                            :src="STAR_ICON"
                            alt="Star"
                            class="h-14 mx-auto mb-3 drop-shadow-lg"
                        />
                        <h2 class="text-3xl font-bold text-white mb-1.5">
                            Get More Stars
                        </h2>
                        <p class="text-white/95 text-sm leading-relaxed max-w-xs mx-auto">
                            Purchase star packs to sponsor your favorite auditions
                        </p>
                    </div>

                    <!-- Current Balance -->
                    <div class="flex items-center justify-center gap-3 py-5 bg-white border-b border-gray-100">
                        <span class="text-sm text-gray-500 font-medium">Your Balance</span>
                        <div class="flex items-center gap-1.5">
                            <img :src="STAR_ICON" alt="" class="w-6 h-6" />
                            <span class="text-3xl font-bold text-gray-900">{{ userBalance }}</span>
                        </div>
                    </div>

                    <!-- Star Packs -->
                    <div class="px-6 pt-5 pb-3">
                        <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                            Choose a Star Pack
                        </p>

                        <div v-if="packs.length > 0" class="grid grid-cols-3 gap-2.5">
                            <button
                                v-for="pack in packs"
                                :key="pack.stars"
                                @click="handlePackSelect(pack)"
                                :class="[
                                    'relative py-4 px-2 rounded-2xl border-2 transition-all active:scale-[0.97]',
                                    selectedPack && selectedPack.stars === pack.stars
                                        ? 'border-amber-500 bg-gray-900 ring-4 ring-amber-500/20'
                                        : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-lg'
                                ]"
                            >
                                <div class="flex flex-col items-center gap-1.5">
                                    <img :src="STAR_ICON" alt="" class="w-7 h-7" />
                                    <span :class="[
                                        'text-lg font-bold',
                                        selectedPack && selectedPack.stars === pack.stars ? 'text-white' : 'text-gray-900'
                                    ]">{{ pack.stars.toLocaleString() }}</span>
                                    <span :class="[
                                        'text-xs font-semibold',
                                        selectedPack && selectedPack.stars === pack.stars ? 'text-gray-400' : 'text-gray-500'
                                    ]">${{ pack.price.toFixed(2) }}</span>
                                </div>
                            </button>
                        </div>

                        <div v-else class="py-6 text-center">
                            <p class="text-sm text-gray-700 font-medium">
                                Star packs are temporarily unavailable.
                            </p>
                            <p class="text-xs text-gray-500 mt-1">
                                Please refresh the page or try again in a few minutes.
                            </p>
                        </div>
                    </div>

                    <!-- Terms of Service Agreement -->
                    <div class="px-6 pt-2 pb-3">
                        <label class="flex items-start gap-2.5 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                v-model="tosAccepted"
                                class="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
                            />
                            <span class="text-xs text-gray-600 leading-relaxed">
                                User agrees to
                                <a
                                    href="https://www.naughtyamerica.com/terms-of-service.html"
                                    target="_blank"
                                    rel="noopener"
                                    @click.stop
                                    class="text-blue-600 hover:text-blue-700 underline underline-offset-2"
                                >Terms of Service</a>
                                by purchasing Stars and will have one year to redeem Stars.
                            </span>
                        </label>
                    </div>

                    <!-- CTA Button -->
                    <div ref="ctaRef" class="px-6 pb-4">
                        <div
                            class="group relative"
                            :class="!canBuy ? 'cursor-not-allowed' : ''"
                        >
                            <!-- Custom tooltip shown on hover when the CTA is disabled. -->
                            <!-- Wrapper catches hover events because the disabled button has pointer-events-none. -->
                            <div
                                v-if="!canBuy"
                                role="tooltip"
                                class="pointer-events-none absolute bottom-full left-1/2 z-10 mb-3 w-max max-w-[260px] -translate-x-1/2 opacity-0 transition-opacity duration-200 ease-out group-hover:opacity-100"
                            >
                                <div class="relative rounded-lg bg-gray-900 px-3 py-2 text-center text-xs font-medium leading-snug text-white shadow-xl ring-1 ring-black/5">
                                    {{ disabledReason }}
                                    <!-- Downward caret -->
                                    <span class="absolute left-1/2 top-full -ml-1.5 h-0 w-0 border-[6px] border-transparent border-t-gray-900"></span>
                                </div>
                            </div>

                            <button
                                @click="handleBuy"
                                :disabled="!canBuy"
                                :class="[
                                    'w-full py-4 font-bold text-lg rounded-xl transition-all active:scale-[0.99]',
                                    canBuy
                                        ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl'
                                        : 'bg-gray-200 text-gray-400 cursor-not-allowed pointer-events-none'
                                ]"
                            >
                                {{ buyButtonText }}
                            </button>
                        </div>
                    </div>

                    <!-- Trust Indicators -->
                    <div class="px-6 pb-6">
                        <div class="flex items-center justify-center gap-3 text-xs text-gray-500 pt-2">
                            <span class="flex items-center gap-1">
                                <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd"/>
                                </svg>
                                Secure Payment
                            </span>
                            <span class="text-gray-300">&bull;</span>
                            <span>Instant Delivery</span>
                            <span class="text-gray-300">&bull;</span>
                            <span>No Subscription</span>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'

const STAR_ICON = 'https://sm.naughtycdn.com/images/landers/prize/NA-Star.png'

const props = defineProps({
    isOpen: {
        type: Boolean,
        required: true
    },
    userBalance: {
        type: Number,
        default: 0
    },
    starPacks: {
        type: Array,
        default: () => []
    }
})

const emit = defineEmits(['close', 'buy', 'select'])

const selectedPack = ref(null)
const tosAccepted = ref(false)
const ctaRef = ref(null)

const packs = computed(() => Array.isArray(props.starPacks) ? props.starPacks : [])

const canBuy = computed(() => !!selectedPack.value && tosAccepted.value)

const disabledReason = computed(() => {
    if (canBuy.value) return ''
    if (!selectedPack.value && !tosAccepted.value) {
        return 'Please select a star pack and accept the Terms of Service to continue.'
    }
    if (!selectedPack.value) return 'Please select a star pack to continue.'
    return 'Please accept the Terms of Service to continue.'
})

// Reset selection and TOS acceptance when modal opens
watch(() => props.isOpen, (open) => {
    if (open) {
        selectedPack.value = null
        tosAccepted.value = false
    }
})

const handlePackSelect = (pack) => {
    selectedPack.value = selectedPack.value?.stars === pack.stars ? null : pack
    if (selectedPack.value) {
        emit('select', selectedPack.value)
        nextTick(() => {
            ctaRef.value?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        })
    }
}

const buyButtonText = computed(() => {
    if (!selectedPack.value) return 'Select a Pack'
    const p = selectedPack.value
    return `Buy ${p.stars.toLocaleString()} Stars for $${p.price.toFixed(2)}`
})

const handleBuy = () => {
    if (!canBuy.value) return
    emit('buy', selectedPack.value)
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}

.modal-enter-active .max-w-md,
.modal-leave-active .max-w-md {
    transition: transform 0.3s ease;
}

.modal-enter-from .max-w-md,
.modal-leave-to .max-w-md {
    transform: scale(0.95);
}
</style>
